import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { Loader2, RefreshCw } from 'lucide-react';
import { getUpworkRecommendedJobs, getUpworkJob } from '../services/upworkMcpService';

function Job({ job }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const showDetails = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (details) return;
    setBusy(true);
    setError(null);
    try { setDetails(await getUpworkJob(job.jobId)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const current = details?.job || job;
  const href = /^\d{6,25}$/.test(job.jobId) ? `https://www.upwork.com/jobs/~02${job.jobId}` : null;
  return (
    <article className="border-b border-gray-200 p-4 last:border-0">
      <h3 className="font-semibold text-gray-900">
        <button type="button" className="text-left hover:text-primary-600" onClick={showDetails} aria-expanded={expanded}>
          {job.title}
        </button>
      </h3>
      <p className="mt-2 text-xs text-gray-500">
        {job.contractTerms?.contractType === 'HOURLY' ? 'Hourly' : 'Fixed price'}
        {job.amount && ` · ${job.amount.currency} ${job.amount.value}`}
        {job.hourlyBudgetMin && ` · $${job.hourlyBudgetMin.displayValue}–$${job.hourlyBudgetMax?.displayValue || '?'}/hr`}
        {job.connectsCost != null && ` · ${job.connectsCost} Connects`}
      </p>
      <p className={`mt-2 whitespace-pre-wrap text-sm text-gray-700 ${expanded ? '' : 'line-clamp-3'}`}>{current.description}</p>
      {expanded && busy && <p role="status" className="mt-2 text-sm">Loading job details…</p>}
      {expanded && error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
      {expanded && details && <p className="mt-2 text-xs text-gray-500">Source: {details.source}</p>}
      {href && <a href={href} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm text-primary-600 hover:underline">View on Upwork</a>}
    </article>
  );
}

export default function UpworkRecommendedFeed() {
  const { canUpdateUpworkCredentials, canReadTenantConfig, canUpdateTenantConfig } = usePermissions();
  const canConnect = canUpdateUpworkCredentials() && (canReadTenantConfig() || canUpdateTenantConfig());
  const [page, setPage] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [revision, setRevision] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    setBusy(true);
    setError(null);
    getUpworkRecommendedJobs(cursor, controller.signal).then((result) => {
      if (!controller.signal.aborted) setPage(result);
    }).catch((err) => {
      if (!controller.signal.aborted) { setError(err); setPage(null); }
    }).finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => controller.abort();
  }, [cursor, revision]);
  return (
    <section aria-label="Upwork recommendations" className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 p-4">
        <div><h2 className="text-sm font-semibold">For you (Upwork)</h2>
          <p className="text-xs text-gray-500">Personalized by Upwork for your selected account.</p></div>
        <button type="button" disabled={busy} onClick={() => { setCursor(null); setRevision((n) => n + 1); }}
          className="flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm disabled:opacity-50">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      {busy ? <p role="status" className="flex items-center gap-2 p-4 text-sm"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading recommendations…</p> : error ? (
        <div role="alert" className="p-4 text-sm text-red-700">
          <p>{error.message}</p>
          {error.status === 409 && canConnect && <Link to="/admin/integrations" className="mt-2 inline-block underline">Connect or reconnect Upwork</Link>}
          {error.status === 409 && !canConnect && <p>Ask your workspace admin for connection access.</p>}
          <button type="button" className="ml-3 underline" onClick={() => setRevision((n) => n + 1)}>Try again</button>
        </div>
      ) : <>
        {!page?.jobs?.length && <p className="p-4 text-sm text-gray-500">Upwork has no recommendations for this account right now.</p>}
        {page?.jobs?.map((job) => <Job key={job.jobId} job={job} />)}
        <div className="flex gap-3 p-4">
          {cursor && <button type="button" onClick={() => setCursor(null)} className="text-sm text-primary-600">Back to first page</button>}
          {page?.hasMore && page.nextCursor && page.nextCursor !== cursor && <button type="button" onClick={() => setCursor(page.nextCursor)} className="rounded-md border px-3 py-1.5 text-sm">Next page</button>}
        </div>
      </>}
    </section>
  );
}
